import { cloneElement, useEffect, useMemo, useState, useContext } from "react";
import { useChangeBooleans, useComponentProperties, useFetchedValues, useResetActions, useResetAttributeTable, useResetComponentProperties, useSaveComponent, useUpdateSyncComponentAttributes } from "../../../Hooks/Display/useComponentProperties";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import usePages, { useSelectedPage, useUpdatePageComponent } from "../../../Hooks/Display/usePages";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import ComponentToolbar from "./ComponentToolbar/ComponentToolbar";
import Scrollable from "../../Shared/Edit/Scrollable";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Checkbox } from "@progress/kendo-react-inputs";
import { DropDownList, ComboBox } from "@progress/kendo-react-dropdowns";
import componentPropertiesMap from "./ComponentFormProperties.json";
import { byAttributeName, getPageByProperty } from "../utils";
import AttributeTable from "../AttributeTable/AttributeTable";
import ActionTable from "../ActionTable/ActionTable";
import ComponentFormSkeleton from "../../Display/Shared/ComponentFormSkeleton";
import useInternalUserCheck from "../../../Hooks/Misc/useInternalUserCheck";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "../../../Renderers/GlobalContext";
import { getAttributes } from "../../../API/Attribute/Attribute";

function Kanban() {

    const formProperties = componentPropertiesMap.Kanban;
    const { user } = useContext(GlobalContext).state;

    const [sortOrderOptions, setSortOrderOptions] = useState([
        { text: "Ascending", value: "Ascending" },
        { text: "Descending", value: "Descending" }
    ])

    const [coverOptions, setCoverOptions] = useState([
        { text: "Fit to Card", value: "FitToCard" },
        { text: "Fill Card", value: "FillCard" }
    ]);

    // hooks
    const [{ pages }] = usePages();
    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const accessedFromDesigner = usePropsStoreValue("accessedFromDesigner");
    const entityId = usePropsStoreValue("entityId");
    const advancedMode = usePropsStoreValue("advancedMode");
    const isInternalUser = useInternalUserCheck(user.email);
    const saveComponent = useSaveComponent();
    const updateSyncedComponentAttributes = useUpdateSyncComponentAttributes();
    const updatePageComponent = useUpdatePageComponent(selectedPage.parentId, selectedPage._id);
    const resetContext = useResetComponentProperties();
    const resetAttributes = useResetAttributeTable();
    const resetActions = useResetActions();

    const [properties, setProperties] = useComponentProperties();
    const [{ generalProperties: fetchedValues }, { updateGeneralFetchedValue }] = useFetchedValues();
    const [{
        isGeneralPropertiesChanged,
        isActionTableChanged,
        isAttributeTableChanged
    }, setChangeProperty] = useChangeBooleans();

    const imageAttributes = useMemo(() => {
        if (fetchedValues.attributes) {
            return fetchedValues.attributes.filter(attribute => attribute.attributeType === "Image");
        }
        return [];
    }, [fetchedValues.attributes])

    const { t, i18n, ready: tReady } = useTranslation([
        "display/common",
        "display/component_toolbar",
        "display/components/cards",
        "display/components/kanban",
        "display/tables/attribute"
    ]);

    useEffect(() => {
        getData();
    }, []);

    useEffect(() => {
        if (tReady) {
            setSortOrderOptions([
                { text: t("sort_order_options.ascending", { ns: "display/components/kanban" }), value: "Ascending" },
                { text: t("sort_order_options.descending", { ns: "display/components/kanban" }), value: "Descending" }
            ])
            setCoverOptions([
                { text: t("cover_image_options.fit_to_card", { ns: "display/components/cards" }), value: "FitToCard" },
                { text: t("cover_image_options.fill_card", { ns: "display/components/cards" }), value: "FillCard" }
            ])
        }
    }, [tReady])

    // componentWillUnmount
    useEffect(() => {
        return () => {
            resetContext();
        }
    }, [])

    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!loading) {
            setIsChanged();
        }
    }, [properties])

    useEffect(() => {
        if (!loading) {
            setPropertiesUsingAttributes(fetchedValues.attributes);
        }
    }, [selectedPage, selectedComponent])

    const [tabSelected, setSelectedTab] = useState(0);

    function handleTabSelect(event) {
        setSelectedTab(event.selected)
    }

    async function getData() {
        const attributes = await getAttributes(entityId);
        if (selectedPage && selectedComponent) {
            setPropertiesUsingAttributes(attributes);
            setChangeProperty("isGeneralPropertiesChanged", false);
            updateGeneralFetchedValue("attributes", attributes);
            setLoading(false);
        }
    }

    function setPropertiesUsingAttributes(attributes) {
        for (const property of formProperties) {
            switch (property) {
                case "primaryGroupByAttributeId": {
                    const attribute = getAttributeById(attributes, selectedComponent[property]);
                    setProperties("primaryGroupBy", attribute);
                    break;
                }
                case "rowColumnGroupByAttributeId": {
                    const attribute = getAttributeById(attributes, selectedComponent[property]);
                    setProperties("rowGroupBy", attribute);
                    break;
                }
                case "sortByAttributeId": {
                    const attribute = getAttributeById(attributes, selectedComponent[property]);
                    setProperties("sortBy", attribute);
                    break;
                }
                case "editPageId": {
                    const page = getPageByProperty(pages, "_id", selectedComponent[property])
                    setProperties("editPage", page);
                    break;
                }
                case "coverImageAttribute": {
                    const attribute = getAttributeById(attributes, selectedComponent[property]);
                    setProperties("coverImageAttribute", attribute);
                    break;
                }
                default:
                    if (
                        selectedComponent[property] !== undefined
                    ) {
                        setProperties(property, selectedComponent[property])
                    }
                    break;
            }
        }
    }

    function getAttributeById(attributes, attributeId) {
        return attributes.find(attribute => attribute._id === attributeId);
    }

    function clearRowGroupBy() {
        setProperties("rowGroupBy", null);
    }

    function handleInputChange(event) {
        if (event.target.name !== "sortOrder") {
            setProperties(event.target.name, event.target.value);
        }
        switch (event.target.name) {
            case "sortOrder":
                setProperties(event.target.name, event.target.value.value);
                break;
            case "primaryGroupBy": {
                if (properties.rowGroupBy && properties.rowGroupBy._id === event.target.value._id) {
                    setProperties("rowGroupBy", null);
                }
                break;
            }
            case "rowGroupBy": {
                if (properties.primaryGroupBy && properties.primaryGroupBy._id === event.target.value._id) {
                    setProperties("primaryGroupBy", null);
                }
                break;
            }
            default:
                break;
        }
    }

    function handleShowDisplayCoverImage(event) {
        if (event.target.value === false) {
            setProperties("coverImageAttribute", "");
            setProperties("coverImageOption", "FitToCard");
        } else {
            if (!properties.coverImageOption) {
                setProperties("coverImageOption", "FitToCard");
            }
        }
        setProperties(event.target.name, event.target.value);
    }

    function handleCoverImageOption(event) {
        setProperties(event.target.name, event.target.value.value);
    }

    function getCoverImageOption() {
        return coverOptions.find(option => option.value === properties.coverImageOption);
    }

    function getGroupByAttributes() {
        const attributes = fetchedValues?.attributes?.filter(attribute => (
            attribute.attributeType === "Picklist String" ||
            attribute.attributeType === "User Identity" ||
            attribute.attributeType === "State Field" ||
            (attribute.attributeType === "Shared Lookup" && attribute.sharedLookupType === "Picklist String") ||
            (attribute.attributeType === "Mirror" &&
                (
                    attribute.attribute.attributeType === "Picklist String" ||
                    attribute.attribute.attributeType === "User Identity" ||
                    attribute.attribute.attributeType === "State Field" ||
                    (attribute.attribute.attributeType === "Shared Lookup" && attribute.attribute.sharedLookupType === "Picklist String")
                )
            )
        )).sort(byAttributeName);
        return attributes;
    }

    function getRowGroupByAttributes() {
        return getGroupByAttributes().filter(attribute => attribute.id !== properties.primaryGroupBy?.id).sort(byAttributeName);
    }

    function getSortByAttributes() {
        return fetchedValues?.attributes?.filter(attribute => attribute.attributeType !== "Html")
            .sort(byAttributeName);
    }

    function setIsChanged() {
        let isChanged = false;
        let hasErrors = false;
        for (const property of formProperties) {
            if (hasErrors) {
                isChanged = false;
                break;
            }
            switch (property) {
                case "primaryGroupByAttributeId": {
                    if (!properties.primaryGroupBy || !properties.primaryGroupBy._id) {
                        hasErrors = true;
                    } else if (
                        properties.primaryGroupBy &&
                        properties.primaryGroupBy._id !== selectedComponent[property]
                    ) {
                        isChanged = true;
                    } else if (
                        selectedComponent.primaryGroupByAttributeId && !properties.primaryGroupBy
                    ) {
                        isChanged = true
                    }
                    break;
                }
                case "rowColumnGroupByAttributeId": {
                    if (
                        properties.rowGroupBy &&
                        properties.rowGroupBy._id !== selectedComponent[property]
                    ) {
                        isChanged = true;
                    } else if (
                        selectedComponent.rowColumnGroupByAttributeId && !properties.rowGroupBy
                    ) {
                        isChanged = true
                    }
                    break;
                }
                case "sortByAttributeId": {
                    if (
                        properties.sortBy &&
                        properties.sortBy._id !== selectedComponent[property]
                    ) {
                        isChanged = true;
                    } else if (
                        selectedComponent.sortByAttributeId && !properties.sortBy
                    ) {
                        isChanged = true
                    }
                    break;
                }
                case "editPageId": {
                    if (!properties.editPage || !properties.editPage._id) {
                        hasErrors = true;
                    } else if (
                        properties.editPage &&
                        properties.editPage._id !== selectedComponent[property]
                    ) {
                        isChanged = true;
                    } else if (
                        selectedComponent.editPageId && !properties.editPage
                    ) {
                        isChanged = true
                    }
                    break;
                }
                case "coverImageAttribute":
                    if (
                        selectedComponent[property] && !properties[property] ||
                        selectedComponent[property] && properties[property] && selectedComponent[property] !== properties[property]._id ||
                        !selectedComponent[property] && properties[property]
                    ) {
                        isChanged = true;
                    }
                    break;
                default:
                    if (properties[property] !== selectedComponent[property]) {
                        isChanged = true;
                        break;
                    }
            }
            if (isChanged) {
                break;
            }
        }
        if (properties.primaryGroupBy && properties.rowGroupBy && properties.primaryGroupBy._id === properties.rowGroupBy._id) {
            isChanged = false;
        }
        // else if (properties.showCoverImage && !properties.coverImageAttribute) {
        //     isChanged = false;
        // }
        setChangeProperty("isGeneralPropertiesChanged", isChanged);
    }

    function getEditPages() {
        const editPages = []
        const { portalId } = selectedPage;
        for (const folder of pages) {
            for (const page of folder.items) {
                if (page.portalId === portalId && page.subPageType === "Edit Record") {
                    editPages.push(page);
                }
            }
        }
        return editPages;
    }

    async function handleSave() {
        const updatedComponent = await saveComponent();
        updatePageComponent(selectedComponent.id, updatedComponent);
        if (isAttributeTableChanged && selectedComponent.componentsToSync && selectedComponent.componentsToSync.length > 0) {
            updateSyncedComponentAttributes();
        }
    }

    function resetComponent() {
        if (isGeneralPropertiesChanged) {
            setPropertiesUsingAttributes(fetchedValues.attributes);
        }
        if (isAttributeTableChanged) {
            resetAttributes();
        }
        if (isActionTableChanged) {
            resetActions();
        }
    }

    function getSortOrderValue(value) {
        return sortOrderOptions.find(option => option.value === value);
    }

    if (loading) {
        return (
            <ComponentFormSkeleton />
        )
    }

    return (
        <div className={"h-100 d-flex flex-column line" + (!accessedFromDesigner && " runtimePopup")}>
            <ComponentToolbar onSave={handleSave} onReset={resetComponent} />
            <Scrollable noPadding={true}>
                <TabStrip className="flex-grow-1 h-100 edit1" selected={tabSelected} onSelect={handleTabSelect}>
                    <TabStripTab title={t("tabs.general", { ns: "display/common" })}>
                        <Scrollable>
                            <form className="p-2 edit2">
                                {
                                    accessedFromDesigner &&
                                    <>
                                        <Checkbox
                                            disabled={selectedComponent.hidden}
                                            checked={properties.showLocalSearch}
                                            name={"showLocalSearch"}
                                            onChange={handleInputChange}
                                            label={t("form_labels.local_search", { ns: "display/components/kanban" })}
                                            className="my-1 d-block"
                                        />
                                        <Checkbox
                                            disabled={selectedComponent.hidden}
                                            name="showLabel"
                                            checked={properties.showLabel}
                                            onChange={handleInputChange}
                                            label={t("form_labels.show_label", { ns: "display/components/kanban" })}
                                            className="my-1 d-block"
                                        />

                                        {/* <Checkbox
                                            disabled={selectedComponent.hidden}
                                            name="InlineEdit"
                                            checked={properties.InlineEdit}
                                            onChange={handleInputChange}
                                            label={"Edit Inline"}
                                            className="my-1 d-block"
                                        /> */}
                                    </>
                                }
                                <div className="row">
                                    <div className="col-4">
                                        <div className="form-label">
                                            {t("form_labels.primary_column_group_by", { ns: "display/components/kanban" })}
                                            {
                                                (selectedComponent.primaryGroupByAttributeId) &&
                                                    (!properties.primaryGroupBy || !properties.primaryGroupBy._id)
                                                    ?
                                                    <span className="text-danger">
                                                        &nbsp;{t("errors.primary_group_by_required", { ns: "display/components/kanban" })}
                                                    </span> : null
                                            }
                                        </div>
                                        <DropDownList
                                            name="primaryGroupBy"
                                            disabled={selectedComponent.hidden}
                                            onChange={handleInputChange}
                                            className="form-control-custom"
                                            value={properties.primaryGroupBy}
                                            data={getGroupByAttributes()}
                                            textField="attributeName"
                                            dataItemKey="_id"
                                        />
                                    </div>
                                    <div className="col-12">
                                        <Checkbox
                                            disabled={selectedComponent.hidden}
                                            name="showCoverImage"
                                            checked={properties.showCoverImage}
                                            value={properties.showCoverImage}
                                            onChange={handleShowDisplayCoverImage}
                                            label={"Display Cover Image"}
                                            className="mt-3 d-block"
                                        />
                                    </div>
                                    {
                                        properties.showCoverImage &&
                                        <div className="col-12">
                                            <div className="col-4 clearBtn">
                                                <div
                                                    className="form-label"
                                                    title="Fit to Card: Scale the image to fit entirely within the card dimensions, which may result in empty spaces (letterboxing) on two sides.
Fill Card: Scale the image to fill the entire card area, cropping parts of the image if necessary."
                                                >
                                                    {t("form_labels.display_cover_image_options", { ns: "display/components/cards" })}
                                                </div>
                                                <ComboBox
                                                    name="coverImageAttribute"
                                                    className="select-overflow"
                                                    disabled={selectedComponent.hidden}
                                                    onChange={handleInputChange}
                                                    value={properties.coverImageAttribute}
                                                    textField='attributeName'
                                                    data={imageAttributes}
                                                    dataItemKey='_id'
                                                />
                                            </div>
                                            <div className="col-4">
                                                <div
                                                    className="form-label"
                                                    title="Fit to Card: Scale the image to fit entirely within the card dimensions, which may result in empty spaces (letterboxing) on two sides.
Fill Card: Scale the image to fill the entire card area, cropping parts of the image if necessary."
                                                >
                                                    {t("form_labels.display_cover_image_options", { ns: "display/components/cards" })}
                                                </div>
                                                <DropDownList
                                                    name="coverImageOption"
                                                    disabled={selectedComponent.hidden}
                                                    value={getCoverImageOption()}
                                                    data={coverOptions}
                                                    textField="text"
                                                    dataItemKey="value"
                                                    onChange={handleCoverImageOption}
                                                />
                                            </div>
                                        </div>
                                    }


                                    {/* <div className="col-4">
                                        <div className="form-label">Secondary Column Group By</div>
                                        <DropDownList
                                            disabled={selectedComponent.hidden}
                                            onChange={hanldeSecondaryGroupBy}
                                            className="form-control-custom"
                                            value={properties.secondaryGroupBy}
                                            data={properties.Attributes}
                                            textField="attributeName"
                                        />
                                    </div> */}
                                    {/* <div className="col-4">
                                        <div className="form-label">
                                            {t("form_labels.row_column_group_by", { ns: "display/components/kanban" })}
                                        </div>
                                        <DropDownList
                                            name="rowGroupBy"
                                            disabled={selectedComponent.hidden}
                                            onChange={handleInputChange}
                                            className="form-control-custom"
                                            value={properties.rowGroupBy}
                                            data={getRowGroupByAttributes()}
                                            textField="attributeName"
                                            dataItemKey="id"
                                            valueRender={(element, value) => (
                                                <ValueRender
                                                    element={element}
                                                    value={value}
                                                    onRemove={clearRowGroupBy}
                                                />
                                            )}
                                        />
                                        {
                                            properties.primaryGroupBy &&
                                            properties.rowGroupBy &&
                                            properties.primaryGroupBy.id === properties.rowGroupBy.id &&
                                            <div className="col-12">
                                                <div className="form-label">
                                                    <span className="text-danger">
                                                        {t("errors.primary_row_column_same", { ns: "display/components/kanban" })}
                                                    </span>
                                                </div>
                                            </div>
                                        }
                                    </div> */}
                                </div>
                                {
                                    accessedFromDesigner &&
                                    <div className="row">
                                        <div className="col-4">
                                            <div className="form-label">
                                                {t("form_labels.sort_by", { ns: "display/components/kanban" })}
                                            </div>
                                            <DropDownList
                                                name="sortBy"
                                                disabled={selectedComponent.hidden}
                                                onChange={handleInputChange}
                                                className="form-control-custom"
                                                value={properties.sortBy}
                                                data={getSortByAttributes()}
                                                textField="attributeName"
                                                dataItemKey="_id"
                                            />
                                        </div>
                                        <div className="col-4">
                                            <div className="form-label">
                                                {t("form_labels.sort_order", { ns: "display/components/kanban" })}
                                            </div>
                                            <DropDownList
                                                name="sortOrder"
                                                disabled={selectedComponent.hidden}
                                                onChange={handleInputChange}
                                                className="form-control-custom"
                                                value={getSortOrderValue(properties.sortOrder)}
                                                data={sortOrderOptions}
                                                textField="text"
                                                dataItemKey="value"
                                            />
                                        </div>
                                    </div>
                                }
                                {
                                    accessedFromDesigner &&
                                    <>
                                        <div className="my-4 border-top"></div>
                                        <div className="row">
                                            <h6>
                                                {t("form_labels.event_actions", { ns: "display/components/kanban" })}
                                            </h6>
                                            <div className="col-4">
                                                <div className="form-label">
                                                    {t("form_labels.edit_page", { ns: "display/components/kanban" })}
                                                </div>
                                                <DropDownList
                                                    name="editPage"
                                                    disabled={selectedComponent.hidden}
                                                    onChange={handleInputChange}
                                                    value={properties.editPage}
                                                    data={getEditPages()}
                                                    textField='pageName'
                                                    dataItemKey="_id"
                                                />
                                            </div>
                                        </div>
                                    </>
                                }
                            </form>
                        </Scrollable>
                    </TabStripTab>
                    <TabStripTab title={t("tabs.properties", { ns: "display/common" })}>
                        <AttributeTable />
                    </TabStripTab>
                    <TabStripTab title={t("tabs.actions", { ns: "display/common" })}>
                        <ActionTable />
                    </TabStripTab>
                </TabStrip>
            </Scrollable>
        </div>
    )
}

const ValueRender = ({ element, value, onRemove }) => {
    if (!value) {
        return element;
    }

    function handleRemove(e) {
        e.stopPropagation();
        onRemove(e);
    }

    const children = [
        <div className="d-flex justify-content-between align-items-center w-100">
            <span className="text-wrap" key={2}>&nbsp; {element.props.children}</span>
            <span key={1} className="fa-solid fa-xmark text-danger" onClick={handleRemove}></span>
        </div>
    ];
    return cloneElement(
        element,
        {
            ...element.props,
        },
        children
    );
};

export default Kanban;